import { CartPage } from "@pages/Cart/CartPage";
import { CustomerDetailsPage } from "@pages/CustomerDetails/CustomerDetailsPage";
import { SummaryPage } from "@pages/Summary/SummaryPage";
import { IStepRoute, StepperRouter } from "@common/StepperRouter";
import { Trans, useTranslation } from "react-i18next";
import { ICart } from "@features/cart/ICart";
import { useMemo, useState } from "react";
import { mapCartToFlyingCart } from "@mappers/cartMapper";
import { hasOfferBroadband } from "@utils/offerUtils";
import { InstallationPage } from "@pages/Installation/InstallationPage";
import { ROUTES } from "@constants";
import { areCustomerDetailsFilled } from "@features/stepper/stepValidationsUtils/customerDetailsValidation";
import { ICustomerDetails } from "@interface/customerDetails/ICustomerDetails";
import { isPaymentStepValid } from "./stepValidationsUtils/paymentValidation";
import { isCartStepValid } from "./stepValidationsUtils/cartValidation";
import { IFetchState, keycloakUtils } from "itsf-ui-common";
import { Typography } from "@mui/material";
import { useLoginLogout } from "@hooks/useLoginLogout";
import { useStyle } from "./style";
import { BUTTON_IDS } from "@features/dataLayer/buttonIds";

interface IParams {
    cart: ICart;
    setCart(cart?: ICart): void;
    customerDetails?: ICustomerDetails;
    setCustomerDetails(customerDetails?: ICustomerDetails): void;
    getCustomerDetailsState: IFetchState;
    getCustomerDetails(): void;
}

export const Stepper = (props: IParams) => {
    const { cart, setCart, customerDetails, setCustomerDetails, getCustomerDetailsState, getCustomerDetails } = props;
    const { t } = useTranslation(["cart", "customerDetails", "glossary", "installation", "summary"]);
    const { logIn } = useLoginLogout();
    const isAuthenticated = keycloakUtils.isAuthenticated();
    const { classes } = useStyle();
    const [userAlreadyExistsEmail, setUserAlreadyExistsEmail] = useState<string>();

    // Map the cart to the format expected by FlyingCart
    const flyingCart = useMemo(() => mapCartToFlyingCart(cart), [cart]);

    const isCartValid = useMemo(() => isCartStepValid(cart), [cart]);

    const isCustomerDetailsValid = useMemo(() => areCustomerDetailsFilled(customerDetails), [customerDetails]);

    const isPaymentValid = useMemo(() => isPaymentStepValid(cart), [cart]);

    const stepList: IStepRoute[] = useMemo(
        () => [
            {
                component: <CartPage cart={cart} flyingCart={flyingCart} setCart={setCart} />,
                path: ROUTES.CART_PAGE_URL,
                label: t("cart:basket"),
            },
            {
                component: (
                    <CustomerDetailsPage
                        cart={cart}
                        customerDetails={customerDetails}
                        flyingCart={flyingCart}
                        getCustomerDetails={getCustomerDetails}
                        getCustomerDetailsState={getCustomerDetailsState}
                        setCustomerDetails={setCustomerDetails}
                        setUserAlreadyExistsEmail={setUserAlreadyExistsEmail}
                        userAlreadyExistsEmail={userAlreadyExistsEmail}
                    />
                ),
                path: ROUTES.DETAILS_PAGE_URL,
                label: t("customerDetails:myDetails"),
                isValid: isCartValid,
                hideStepTitle: Boolean(userAlreadyExistsEmail),
                ...(!isAuthenticated && {
                    subtitle: (
                        <Trans
                            components={{
                                loginLink: (
                                    <Typography
                                        className={classes.subtitleLoginLink}
                                        color="primary"
                                        component="span"
                                        variant="large"
                                        onClick={() => logIn()}
                                    />
                                ),
                            }}
                            i18nKey="myDetailsSubtitle"
                            ns="customerDetails"
                        />
                    ),
                }),
                id: BUTTON_IDS.CHECKOUT_CTA.MY_DETAILS,
            },
            ...(hasOfferBroadband(cart.offers)
                ? [
                      {
                          component: <InstallationPage cart={cart} flyingCart={flyingCart} setCart={setCart} />,
                          path: ROUTES.INSTALLATION_PAGE_URL,
                          label: t("installation:install"),
                          isValid: isCartValid && isCustomerDetailsValid,
                          id: BUTTON_IDS.CHECKOUT_CTA.INSTALLATION,
                      },
                  ]
                : []),
            {
                component: <SummaryPage cart={cart} flyingCart={flyingCart} />,
                path: ROUTES.SUMMARY_PAGE_URL,
                label: t("glossary:payment"),
                subtitle: t("summary:monthlyBillChargedCard"),
                isValid: isCartValid && isCustomerDetailsValid && isPaymentValid,
                id: BUTTON_IDS.CHECKOUT_CTA.PAYMENT,
            },
        ],
        [
            cart,
            classes.subtitleLoginLink,
            customerDetails,
            flyingCart,
            getCustomerDetails,
            getCustomerDetailsState,
            isAuthenticated,
            isCartValid,
            isCustomerDetailsValid,
            isPaymentValid,
            logIn,
            setCart,
            setCustomerDetails,
            t,
            userAlreadyExistsEmail,
        ]
    );

    return <StepperRouter stepList={stepList} />;
};
